<template lang="pug">
.page.catalog(ref="page")
  .inner-gap
    // Search Category
    template(v-if="category && popularProducts.length")
      h2(v-if="category.name") Discover {{ productType?.name || category.name }} popular products
      ProductSlider(:products="popularProducts")

    template(v-if="category && popularBrands.length" )
      h2 Discover {{ productType?.name || category.name }} popular brands
      BrandSlider(:brands="popularBrands")

    // searched Exact Brand
    template(v-if="searchedBrand && searchedBrandProducts.length")
      .layout.flex.mt-4
        .aside
          BrandOverview(:data="searchedBrand" linked social-links)
        .results
          ProductSlider(:products="searchedBrandProducts")

    template(v-if="searchedBrand && searchedBrands.length")
      h2 Similar brands
      BrandSlider.mt-2(:brands="searchedBrands")

    .layout.flex
      .aside
        products-side-bar

      .results(ref="results")
        client-only
          pre-approval-buyer-warning
          mov-alert-headup

        .recommend(v-if="!hasQuery && recommendedDirectSkus.length")
          .page__shelf
            .page__title Recommended for You
          .slide-container
            .weekly-promo(v-if="weeklyPromo" ref="weekly")
              a.frame(href="#" @click.prevent="openWeeklyPromo")
                .img
                  img.weekly-img(:src="weeklyPromo.image_url")
                .info Check out our staff-picked
                  br
                  | weekly promotions 🎉

            .slide
              swiper.swiper-area(
                ref="swiper"
                :loop="true"
                :options="swiperOptions"
                :auto-destroy="false"
                :key="swiperSlideKey")
                swiper-slide.swiper-slide(v-for="i, k in recommendedDirectSkus" :key="k")
                  product-card.slide__item(
                    :productCardType="PRODUCT_CARD_TYPES.RECOMMENDATION_CAROUSEL_CATALOG"
                    show-variant
                    is-variant
                    image-path="image_urls.product"
                    :data="i"
                    :trackProps="{zone: 'CATALOG_RECOMMENDED_PRODUCTS', position: k + 1, recommendation_model: 'NEW'}")

              span.slide__previous(@click="$refs.swiper.swiperInstance.slidePrev()")
                i.bx.bx-chevron-left.bx-5
              span.slide__next(@click="$refs.swiper.swiperInstance.slideNext()")
                i.bx.bx-chevron-right.bx-5

        .new(v-if="!hasQuery" :class="{ 'negative-margin': recommendedDirectSkus.length }")
          .page__shelf
            .page__title New on Pod
            nuxt-link.linked(:to="{ name: 'products-new' }" v-click="() => $amplitude.track('CLICK_EXPLORE_MORE')") Explore more →
          products-grid.single-row(
            :records="newProducts"
            :loading="loading.new"
            :placeholders="7"
            trackProps="CATALOG_NEW")

        .page__shelf
          div
            .default-catalog(v-if="category")
              nuxt-link.page__back.linked.pf-inline-block.mb-1(:to="$links.catalog()") &larr; Back to default catalog
              h1.page__title.mt-1 All {{ category.name }} products
            .vendor-company(v-else-if="mov")
              nuxt-link.page__back.linked.pf-inline-block.mb-1(:to="$links.catalog()") &larr; Back to default catalog
              h1.page__title.mt-1 {{ mov.title }}'s products
            .default-catalog(v-else)
              h1.page__title(v-if="searchedBrand && searchedBrandProducts.length") Related Results
              h1.page__title(v-else) {{ hasQuery ? 'Catalog' : 'Most Popular'}}

            .page__description.paginator
              template(v-if="paginator") Showing&nbsp;
                span.count {{ records.length }}
                | &nbsp;over&nbsp;
                span.total {{ paginator.total }}
                | &nbsp;{{ paginator.total > 1 ? 'products' : 'product' }}
              template(v-else) -

        filters-bar

        .sort-area.tr.mb-2(v-if="hasQuery")
          el-select.sort.vam(v-model="sort" :placeholder="isVendor ? 'Order by Brand name - A > Z' : 'Order by Popularity'")
            el-option(v-if="!isVendor" value="relevance_score,desc" label="Order by Popularity")
            el-option(v-if="canOrderedByActivatedDate" value="activated_at,desc" label="Order by Newest")
            el-option(value="brand_name,asc" label="Order by Brand name - A > Z")
            el-option(value="brand_name,desc" label="Order by Brand name - Z > A")
            el-option(value="name,asc" label="Order by Product name - A > Z")
            el-option(value="name,desc" label="Order by Product name - Z > A")
            template(v-if="isBuyer && !isPreApprovalBuyer")
              el-option(value="price,asc" label="Order by Price - Lower first")
              el-option(value="price,desc" label="Order by Price - Higher first")
            el-option(
              v-if="hasSearchTerm"
              value="relevance,desc"
              label="Order by Relevance")

          el-button.filter.vam.hidden-md-and-up.ml-1(size="small" @click="showFiltersDrawer = !showFiltersDrawer")
            i.pf-icon.icon.bx.bx-filter-alt

        infinite-fetcher(
          :fetch="fetch"
          :paginator="paginator"
          loading-text="Loading more products...")
          products-grid(:records="records" :loading="loading.main" trackProps="CATALOG")

          empty(
            v-if="!records.length && !loading.main"
            title="No results found"
            type="empty.catalog")
            nuxt-link.el-button.el-button--small(
              :to="$links.catalog()") Clear all filters
            nuxt-link.el-button.el-button--small.el-button--primary(
              v-if="isBuyer"
              :to="{name: 'brands-invite'}"
              title="Invite brands") Invite Brands →

          .ender
</template>

<script>
import { debounce, isEmpty } from 'lodash'
import { mapGetters } from 'vuex'
import { DEBOUNCE_DELAY, PRODUCT_CARD_TYPES } from '~/settings'

import InfiniteFetcher from '~/components/InfiniteFetcher'
import ProductsSideBar from '~/components/menus/ProductsSideBar'
import FiltersBar from '~/components/menus/FiltersBar'
import MovAlertHeadup from '~/components/MovAlertHeadup'
import ProductsGrid from '~/components/products/ProductsGrid'
import PreApprovalBuyerWarning from '~/components/PreApprovalBuyerWarning'
import ProductCard from '~/components/products/ProductCard'
import BrandOverview from '~/components/catalog/BrandOverview'
import WeeklyPromoModal from '~/components/modals/WeeklyPromoModal'
import ProductSlider from '~/components/catalog/ProductSlider'
import BrandSlider from '~/components/catalog/BrandSlider'

import { Swiper as SwiperClass, Autoplay } from 'swiper/core'
import getAwesomeSwiper from 'vue-awesome-swiper/dist/exporter'
SwiperClass.use([Autoplay])
const { Swiper, SwiperSlide } = getAwesomeSwiper(SwiperClass)
const SWIPER_DEFAULT_OPTIONS = {
  autoplay: {
    delay: 4000
  },
  slidesPerView: 4,
  spaceBetween: 10
}

export default {
  name: 'Catalogs',

  key (route) {
    return route.fullPath
  },

  components: {
    InfiniteFetcher,
    ProductsSideBar,
    FiltersBar,
    MovAlertHeadup,
    ProductsGrid,
    PreApprovalBuyerWarning,
    ProductCard,
    Swiper,
    SwiperSlide,
    BrandOverview,
    WeeklyPromoModal,
    ProductSlider,
    BrandSlider
  },

  provide: {
    selectSkuByImageUrl: true
  },

  layout: 'catalog',

  data () {
    return {
      records: [],
      newProducts: [],
      recommendedDirectSkus: [],
      popularProducts: [],
      popularBrands: [],
      searchedBrandProducts: [],
      searchedBrands: [],
      paginator: null,
      loading: {},
      showFiltersDrawer: false,
      PRODUCT_CARD_TYPES,
      swiperSlideKey: 'default-swiper-slide-key',
      swiperOptions: SWIPER_DEFAULT_OPTIONS,
      searchedBrand: null,
      debouncedResize: null
    }
  },

  fetch () {
    const promises = [this.fetch()]

    if (this.isBuyer && this.isApprovedCompany) promises.push(this.getRecommendationDirectSKUs())

    if (!this.isVendor && this.category) {
      promises.push(this.getPopularProducts())
      promises.push(this.getPopularBrands())
    }

    if (!this.hasQuery) promises.push(this.getNewProducts())

    return Promise
      .all(promises)
      .catch(e => {
        this.$nuxt.error({
          statusCode: e?.response?.status || 404
        })
      })
  },

  head () {
    return {
      title: this.title
    }
  },

  computed: {
    ...mapGetters({
      isBuyer: 'authentication/isBuyer',
      isApprovedCompany: 'authentication/isApprovedCompany',
      isHeadBuyer: 'authentication/isHeadBuyer',
      headBuyerHasAssignedRegions: 'authentication/headBuyerHasAssignedRegions',
      isVendor: 'authentication/isVendor',
      isPreApprovalBuyer: 'authentication/isPreApprovalBuyer',
      movs: 'cart/movs',
      useKailuaApi: 'master/useKailuaApi',
      weeklyPromo: 'master/weeklyPromo'
    }),

    title () {
      if (this.mov && this.mov.title) return `${this.mov.title}'s products`
      return 'Catalog'
    },

    mov () {
      const company = +this.$route.query?.['q[vendor_company_id]']
      const mov = this.movs.find(i => i.company === company)
      return mov
    },

    category () {
      const id = parseInt(this.$route.query.category_id, 10)

      return this.$store.getters['master/productCategoryById'](id)
    },

    productType () {
      const id = parseInt(this.$route.query['q[product_type_id]'], 10)

      return this.$store.getters['master/productTypeById'](id)
    },

    sort: {
      get () {
        const { sort, direction } = this.$route.query

        if (sort && direction) return `${sort},${direction}`

        if (this.hasSearchTerm && this.useKailuaApi) return 'relevance,desc'

        if (this.isVendor) return 'brand_name,asc'

        if (this.canOrderedByActivatedDate) return 'activated_at,desc'

        return 'relevance_score,desc'
      },

      set (val) {
        if (typeof val !== 'string') return
        const [sort, direction] = val.split(',')
        this.$router.push({
          name: this.$route.name,
          query: {
            ...this.$route.query,
            ...sort ? { sort } : {},
            ...direction ? { direction } : {}
          }
        })?.catch(this.$debug.log)
      }
    },

    hasQuery () {
      return !isEmpty(this.$route.query)
    },

    hasSearchTerm () {
      const term = this.$route.query.term || ''
      return !!term.trim().length
    },

    canOrderedByActivatedDate () {
      return this.isBuyer || this.headBuyerHasAssignedRegions
    }
  },

  watch: {
    weeklyPromo () {
      this.resize()
    },
    '$route.query': {
      handler (newQuery, oldQuery) {
        if (this._inactive || JSON.stringify(newQuery) === JSON.stringify(oldQuery))
          return

        this.$fetch()
      }
    }
  },

  mounted () {
    this.debouncedResize = debounce(this.resize, DEBOUNCE_DELAY)
    this.$bus.$on('re-render-catalog-recommended-product-swiper-slide', this.debouncedResize)

    window.addEventListener('resize', this.debouncedResize)
    this.resize()
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.debouncedResize)
    this.$bus.$off('re-render-catalog-recommended-product-swiper-slide')
  },

  methods: {
    resize () {
      if (!this.$refs.results) return

      const resultsWidth = this.$refs.results.clientWidth

      const isSM = window.innerWidth > 768
      const isLG = window.innerWidth > 1200
      const gap = 10
      const slideWidth = isLG ? 210 : isSM ? 190 : 150
      const slideNum = Math.floor(resultsWidth / (slideWidth + gap))

      const weeklyWidth = resultsWidth / slideNum
      const slidesPerView = this.weeklyPromo && isSM ? slideNum - 1 : slideNum

      this.swiperOptions = { ...SWIPER_DEFAULT_OPTIONS, slidesPerView }

      if (this.$refs.weekly)
        this.$refs.weekly.style.width = isSM ? `${weeklyWidth - gap}px` : '100%'

      this.swiperSlideKey = Date.now()
    },

    fetch (query = {}, { clear = true } = {}) {
      if (clear) this.$set(this.loading, 'main', true)

      let { sort, direction } = this.$route.query

      if (!sort || !direction) [sort, direction] = this.sort.split(',')

      const queryParams = {
        ...query,
        ...this.$route.query,
        sort,
        direction
      }

      // save search term history
      if (this.hasSearchTerm)
        this.$store.commit('history/addKeyword', this.$route.query.term)

      // remove relevance sorting if no search term entered
      if (!this.hasSearchTerm && queryParams.sort === 'relevance') {
        delete queryParams.sort
        delete queryParams.direction
      }

      return this.$api.catalog
        .searchProducts(queryParams)
        .then(res => {
          this.paginator = this.$paginators.parse(res)
          this.records = [
            ...(clear ? [] : this.records),
            ...res.results
          ]
          // User searches for an exact brand name & fetch more
          if (queryParams.term) {
            this.searchedBrand = res.brand || null
            if (!queryParams.page || +queryParams.page < 2) {
              if (res.brand) {
                this.$amplitude?.track('SEARCH_EXACT_BRAND_NAME', { keyword: queryParams.term, brand_id: res.brand.id })

                return Promise.all([this.getSimilarSearchedBrand(), this.getSearchedBrandProducts()])
              }
            } else {
              this.$amplitude?.track('SEARCH_FETCH_MORE', {
                keyword: queryParams.term,
                page: queryParams.page,
                brand_id: res.brand?.id || null,
                results_length: this.records.length
              })
            }
          }
        })
        .catch(e => {
          this.$debug.log(e)
          const error = { statusCode: e.response?.status || 404 }
          if (process.server) {
            this.$nuxt.context.res.statusCode = error.statusCode
            this.$fetchState.error = error
          } else {
            this.$nuxt.error(error)
          }
        })
        .finally(() => {
          this.$set(this.loading, 'main', false)
        })
    },

    getSimilarSearchedBrand () {
      return this.$api.catalog.searchBrands({
        'q[similar_brands_for_id]': this.searchedBrand.id,
        per_page: 20
      })
        .then(({ results }) => {
          this.searchedBrands = results
        })
    },

    getSearchedBrandProducts () {
      return this.$api.catalog.searchProducts({
        brand_id: this.searchedBrand.id,
        per_page: 12
      })
        .then(({ results }) => {
          this.searchedBrandProducts = results
        })
    },

    getPopularProducts () {
      return this.$api.catalog.searchProducts({
        category_id: this.category.id,
        'q[product_type_id]': this.productType?.id,
        per_page: 12,
        sort: 'relevance_score',
        direction: 'desc'
      })
        .then(({ results }) => {
          this.popularProducts = results
        })
    },

    getPopularBrands () {
      return this.$api.catalog.searchBrands({
        'q[product_category_ids]': [this.category.id],
        'q[product_type_ids]': [this.productType?.id]
      })
        .then(({ results }) => {
          this.popularBrands = results
        })
    },

    getRecommendationDirectSKUs () {
      this.$set(this.loading, 'recommend', true)

      return this.$api.buyer.getRecommendationDirectSKUs()
        .then(({ results }) => {
          this.recommendedDirectSkus = results.map(e => ({
            ...e,
            has_warehoused_variant: e.is_warehoused_variant,
            promotion_types: e.promotions?.length && e.promotions.map(e => e.promotion_type)
          }))
        }).finally(() => {
          this.$set(this.loading, 'recommend', false)
        })
    },

    getNewProducts () {
      this.$set(this.loading, 'new', true)

      return this.$api.catalog.searchProducts({
        'q[new_product]': true,
        per_page: 7
      }).then(({ results }) => {
        this.newProducts = results
      }).finally(() => {
        this.$set(this.loading, 'new', false)
      })
    },

    openWeeklyPromo () {
      this.$amplitude.track('CLICK_WEEKLY_PROMO', { zone: 'CATALOG_RECOMMENDED' })
      this.$dialogs.open({
        parent: this,
        component: WeeklyPromoModal
      })
    }
  }
}
</script>

<style lang="scss" src="~/assets/new-products.scss"></style>
<style lang="scss" scoped>
.slide-container {
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  align-items: stretch;
  > * {
    min-width: 0;
  }

  @include media(sm) {
    flex-direction: row;
  }
}
.layout {
  gap: 30px;
  .aside {
    flex: 0 200px;
    background: #f4f6f8;
    @include media(sm-down) {
      display: none;
    }
  }
  .results {
    flex: 1;
    .page__shelf {
      .flex {
        gap: 12px;
        @include media(xs-only) {
          width: 100%;
        }
      }
    }
  }
  .sort {
    flex: 1;
    @include media(sm) {
      flex: 0 auto;
    }
  }
  .layout.el-radio-group {
    ::v-deep .el-radio-button__inner {
      padding: 6px 10px;
    }
  }
  .filter {
    padding: 6px 10px;
  }
}
.paginator {
  .count, .total {
    font-weight: $--font-weight-bold;
    color: $--color-primary;
    margin: 0 .35em;
  }
}
.el-drawer__wrapper {
  ::v-deep {
    .product-aside-mobile-drawer {
      background: #f4f6f8;
      .el-drawer__body {
        min-height: 0;
      }
      .products-side-bar {
        height: 100%;
        margin-top: 0;
        padding: 0 12px;
        overflow-y: scroll;
      }
    }
  }
}
.weekly-promo {
  display: flex;
  width: 240px;
  max-height: 500px;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid transparent;
  margin-bottom: 10px;

  &:hover {
    border-color: $--color-primary;
  }

  @include media(sm) {
    margin-right: 10px;
    margin-bottom: 0;
  }

  .frame {
    display: flex;
    flex: 1;
    background: white;
    justify-content: space-between;
    @include media(sm) {
      flex-direction: column;
    }

    .info {
      font-weight: $--font-weight-bold;
      padding: 16px 12px;
      text-align: center;
      align-items: center;
      justify-content: center;
      display: flex;

      @include media(sm-down) {
        flex: 3
      }
    }
    .img {
      flex: 1;
      padding: 8px;

      @include media(sm) {
        padding: 8px 8px 0;
      }
      .weekly-img {
        width: 100%;
        object-fit: cover;
        aspect-ratio: 1;
        display: block;
        height: 100%;
        border-radius: 8px;
      }
    }
  }
}
.slide {
  flex: 1;
  position: relative;
  &__previous, &__next {
    border-radius: 50%;
    cursor: pointer;
    position: absolute;
    width: 28px;
    height: 28px;
    line-height: 28px;
    display: block;
    font-size: 20px;
    color: $--color-text-secondary;
    text-align: center;
    background-color: white;
    z-index: 1;
    border: $--border-light;
    top: 50%;
    transform: translateY(-50%);
    &:hover {
      font-weight: $--font-weight-bold;
    }
  }
  &__previous {
    left: -8px;
    @include media(md) {
      left: -14px;
    }
  }
  &__next {
    right: -8px;
    @include media(md) {
      right: -14px;
    }
  }
  .swiper-area {
    transition: opacity .3s ease-in-out;
    &:not(.swiper-container-initialized) {
      opacity: 0;
      display: flex;
      flex-wrap: nowrap;
      height: 410px;
    }
  }
}
</style>
